import React, { forwardRef, useEffect, useImperativeHandle, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Control, FieldErrors, useForm, UseFormSetValue, useWatch } from 'react-hook-form';
import { ControlledInput } from 'src/components/ui/Input/Input';
import Button, { ButtonToneProps } from 'src/components/ui/Button/Button';
import { Tooltip as MuiTooltip } from '@mui/material';

import { useLot } from 'src/services/lots';
import {
  LotFormValues,
  LotsFormValues,
  lotsSchema,
  lotToFormValues,
} from 'src/components/layouts/ProductOverlay/ProductFormTypes';
import { zodResolver } from '@hookform/resolvers/zod';
import { ControlledSegmentedControl } from 'src/components/ui/SegmentedControl/SegmentedControl';
import WarningBox, { WarningBoxColorType } from 'src/components/ui/WarningBox/WarningBox';
import Badge, { BadgeType } from 'src/components/ui/Badge/Badge';
import Divider from 'src/components/ui/Divider/Divider';
import Icon from 'src/components/ui/Icon/Icon';

import { diffEntities } from 'src/services/utils/diffEntities';
import classNames from 'classnames';
import dayjs from 'dayjs';
import { DatePicker } from 'src/components/ui/DatePicker/DatePicker';
import styles from './ProductLotEdit.module.scss';

export function createEmptyLot(generateId: () => number = () => Math.floor(Math.random() * 10000000)): LotFormValues {
  return {
    id: generateId(),
    name: '',
    removalDate: null,
    isLocked: false,
    quantity: 0,
  };
}

type OnSaveParams = {
  lotsToCreate: LotFormValues[];
  lotsToDelete: number[];
  lotsToUpdate: LotFormValues[];
  lotsSelectedIds: number[];
};

const LotStatusBadge: React.FC<{ isLocked: boolean }> = ({ isLocked }) => {
  const { t } = useTranslation();
  return isLocked ? (
    <Badge label={t('lots.is_locked')} type={BadgeType.Error} expanded icon="lock" iconFirst />
  ) : (
    <Badge label={t('lots.unlocked')} type={BadgeType.Successful} expanded />
  );
};

const SelectedLotCard: React.FC<{
  lot: LotFormValues;
  idx: number;
  control: Control<LotsFormValues>;
  errors: FieldErrors<LotsFormValues>;
  setValue: UseFormSetValue<LotsFormValues>;
  /** True while the lot only exists client-side (create mode). */
  isCreateMode: boolean;
  onCancelCreate: () => void;
}> = ({ lot, idx, control, errors, setValue, isCreateMode, onCancelCreate }) => {
  const { t } = useTranslation();

  // Bestehende Chargen sind hier reine Anzeige — bearbeitet wird nur beim Anlegen.
  if (!isCreateMode) {
    return (
      <div className="border-subtle rounded-base surface-elevated p-16 mb-24">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
          <div className="flex flex-col gap-8">
            <span className="typo-text-14--bold text-faded">{t('lots.lot_name')}</span>
            <p className="typo-text-14 text-full">{lot.name}</p>
          </div>
          <div className="flex flex-col gap-8">
            <span className="typo-text-14--bold text-faded">{t('lots.removal_date')}</span>
            <p className="typo-text-14 text-full">
              {lot.removalDate ? dayjs(lot.removalDate).format('DD.MM.YYYY') : '–'}
            </p>
          </div>
        </div>
        <Divider className="my-16" />
        {/* gleiches Grid wie die Feldzeile, damit der Status bündig unter dem Datumsfeld sitzt */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <div className="flex flex-col gap-2">
            <span className="typo-text-14--bold text-faded">{t('lots.status_label')}</span>
            <span className="typo-text-12 text-faded">{t('lots.status_hint')}</span>
          </div>
          <div className="flex items-center">
            <LotStatusBadge isLocked={lot.isLocked} />
          </div>
        </div>
        {lot.isLocked && (
          <WarningBox
            className="mt-16"
            colorType={WarningBoxColorType.Error}
            icon="lock"
            title={t('lots.locked_banner_title')}
            copy={t('lots.locked_banner_copy')}
          />
        )}
      </div>
    );
  }

  return (
    <div className="border-subtle rounded-base surface-elevated p-16 mb-24">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-16">
        <div>
          <ControlledInput
            control={control}
            name={`lots.${idx}.name`}
            label={t('lots.lot_name')}
            error={!!errors.lots?.[idx]?.name}
            errorMessage={errors.lots?.[idx]?.name?.message}
            placeholder={t('lots.name_placeholder')}
            maxLength={200}
          />
          <div className="flex items-center gap-4 mt-8 text-faded">
            <Icon name="infoOutlined" width={14} height={14} />
            <p className="typo-text-12">{t('lots.exact_name_hint')}</p>
          </div>
        </div>
        <DatePicker
          label={t('lots.removal_date')}
          error={!!errors.lots?.[idx]?.removalDate}
          errorMessage={errors.lots?.[idx]?.removalDate?.message}
          defaultValue={lot.removalDate ? dayjs(lot.removalDate) : null}
          onChange={newVal => {
            if (newVal) {
              const datestring = dayjs(newVal).startOf('day').format('YYYY-MM-DDTHH:mm:ss');
              setValue(`lots.${idx}.removalDate`, datestring, { shouldValidate: true });
            } else {
              setValue(`lots.${idx}.removalDate`, null, { shouldValidate: true });
            }
          }}
        />
      </div>
      <Divider className="my-16" />
      {/* gleiches Grid wie die Feldzeile, damit der Umschalter bündig unter dem Datumsfeld sitzt */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
        <div className="flex flex-col gap-2">
          <span className="typo-text-14--bold text-faded">{t('lots.status_label')}</span>
          <span className="typo-text-12 text-faded">{t('lots.status_hint')}</span>
        </div>
        <div className="flex items-center">
          <ControlledSegmentedControl
            control={control}
            name={`lots.${idx}.isLocked`}
            ariaLabel={t('lots.status_label')}
            options={[
              { value: false, label: t('lots.unlocked') },
              { value: true, label: t('lots.is_locked'), icon: 'lock', danger: true },
            ]}
          />
        </div>
      </div>
      {lot.isLocked && (
        <WarningBox
          className="mt-16"
          colorType={WarningBoxColorType.Error}
          icon="lock"
          title={t('lots.locked_banner_title')}
          copy={t('lots.locked_banner_copy')}
        />
      )}
      <div className="flex justify-end mt-16">
        <Button tone={ButtonToneProps.SECONDARY} title={t('lots.cancel')} onClick={onCancelCreate} />
      </div>
    </div>
  );
};

const LotTableRow: React.FC<{
  lot: LotFormValues;
  onSelectClick: () => void;
  disabled: boolean;
  tooltip?: string;
}> = ({ lot, onSelectClick, disabled, tooltip }) => {
  const { t } = useTranslation();
  const selectButton = (
    <Button tone={ButtonToneProps.SECONDARY} title={t('lots.select')} onClick={onSelectClick} disabled={disabled} />
  );

  return (
    <tr className={`hover:surface-elevated-more ${styles.row}`}>
      <td className="typo-text-14--bold text-full py-2 px-4">{lot.name}</td>
      <td className="typo-text-14 text-faded py-2 px-4">
        {lot.removalDate ? dayjs(lot.removalDate).format('DD.MM.YYYY') : '–'}
      </td>
      <td className="typo-text-14 text-full text-left py-2 px-4">{lot.quantity}</td>
      {/* eslint-disable-next-line jsx-a11y/control-has-associated-label -- Label steckt im Badge, für die Regel unsichtbar */}
      <td className="py-2 px-4">
        <LotStatusBadge isLocked={lot.isLocked} />
      </td>
      <td className="py-2 px-4">
        <div className="flex justify-end">
          {tooltip ? (
            <MuiTooltip title={tooltip}>
              <div>{selectButton}</div>
            </MuiTooltip>
          ) : (
            selectButton
          )}
        </div>
      </td>
    </tr>
  );
};

export interface ProductLotEditRef {
  triggerValidation: (cb: (diff: OnSaveParams | null) => void) => void;
  reset: () => void;
}

export interface ProductLotEditProps {
  productId: number;
  defaultSelectedIds?: number[];
  onValidityChange?: (isValid: boolean) => void;
  onSelectedIdsChange?: (selectedIds: Set<number>) => void;
  /** When false, hides the "create lot" action. Lots may only be created in the inbound flow. */
  allowCreate?: boolean;
  /**
   * Called when the user picks an existing lot via its "select" button. The pick is final —
   * the parent applies it immediately (no separate save step). Without this callback the
   * lot is only marked as selected and shown in the read-only card.
   */
  onDirectSelect?: (lotId: number) => void;
}

export const ProductLotEdit = forwardRef<ProductLotEditRef, ProductLotEditProps>(
  (
    { productId, defaultSelectedIds = [], onValidityChange, onSelectedIdsChange, allowCreate = true, onDirectSelect },
    ref,
  ) => {
    const { lots: fetchedLots } = useLot(productId);

    const { t } = useTranslation();

    const {
      control,
      reset,
      setValue,
      formState: { isDirty, errors, isValid },
      trigger,
    } = useForm<LotsFormValues>({
      resolver: zodResolver(lotsSchema),
      defaultValues: { lots: (fetchedLots ?? []).map(lot => lotToFormValues(lot)) },
      mode: 'onChange',
      reValidateMode: 'onChange',
    });

    useEffect(() => {
      onValidityChange?.(isValid);
    }, [isValid, onValidityChange]);

    useEffect(() => {
      if (!isDirty && fetchedLots) {
        const transformed = fetchedLots.map(lot => lotToFormValues(lot));
        reset({ lots: transformed });
      }
    }, [fetchedLots]);

    const [selectedIds, setSelectedIds] = useState<Set<number>>(new Set(defaultSelectedIds));

    const [editModeId, setEditModeId] = useState<number | null>(null);

    useEffect(() => {
      onSelectedIdsChange?.(selectedIds);
    }, [Array.from(selectedIds).join(','), onSelectedIdsChange]);

    const lots = useWatch({ control, name: 'lots' });

    // Eine Selektion zählt nur, wenn sie auf eine vorhandene Charge auflöst — stale IDs
    // (Zeile ohne Charge, anderswo gelöschte Charge) dürfen keine leere Sektion rendern.
    const hasSelectedLot = (lots ?? []).some(lot => selectedIds.has(lot.id));

    useImperativeHandle(ref, () => ({
      reset: () => {
        reset({ lots: (fetchedLots ?? []).map(lot => lotToFormValues(lot)) });
        setSelectedIds(new Set(defaultSelectedIds));
        setEditModeId(null);
      },
      triggerValidation: async cb => {
        const valid = await trigger();
        if (valid) {
          const oldLots = (fetchedLots ?? []).map(lot => lotToFormValues(lot));
          const { toCreate, toDelete, toUpdate } = diffEntities({
            oldEntities: oldLots,
            newEntities: lots ?? [],
            key: lot => lot.id,
          });
          cb({
            lotsToCreate: toCreate,
            lotsToDelete: toDelete.map(l => l.id),
            lotsToUpdate: toUpdate,
            lotsSelectedIds: Array.from(selectedIds),
          });
        } else {
          cb(null);
        }
      },
    }));

    return (
      <>
        <div className={classNames(styles.root, 'mb-16')}>
          {/* Only warn once the merchant actually typed a name — an empty name means
              "no lot" and never reaches the backend, so the exact-match warning doesn't apply. */}
          {editModeId !== null && lots?.find(l => l.id === editModeId)?.name?.trim() && (
            <WarningBox
              className="mb-16"
              colorType={WarningBoxColorType.Warning}
              icon="warning"
              title={t('lots.exact_name_warning_title')}
              copy={t('lots.exact_name_warning_copy')}
            />
          )}
          {hasSelectedLot && (
            <>
              <h4 className="typo-text-14--bold text-faded pb-8">{t('lots.selected_lot')}</h4>
              {lots?.map((item, idx) => {
                if (!selectedIds.has(item.id)) {
                  return null;
                }
                return (
                  <SelectedLotCard
                    key={item.id}
                    lot={item}
                    idx={idx}
                    control={control}
                    errors={errors}
                    setValue={setValue}
                    isCreateMode={editModeId === item.id}
                    onCancelCreate={() => {
                      // kein structuredClone: fehlt in der jsdom-Testumgebung; Lots sind flache Datenobjekte
                      const newLots = lots.map(l => ({ ...l }));
                      newLots.splice(idx, 1);
                      setValue('lots', newLots);
                      setEditModeId(null);
                      setSelectedIds(new Set([]));
                      trigger();
                    }}
                  />
                );
              })}
            </>
          )}
          <div className="flex items-center justify-between pb-8">
            <h4 className="typo-text-14--bold text-faded">{t('lots.product_lots')}</h4>
            {allowCreate && (
              <Button
                icon="plus"
                tone={ButtonToneProps.SECONDARY}
                title={t('lots.create')}
                disabled={editModeId !== null}
                onClick={() => {
                  const newLots = lots ? lots.map(l => ({ ...l })) : [];
                  const emptyLot = createEmptyLot();
                  newLots.push(emptyLot);
                  setValue('lots', newLots);
                  setSelectedIds(new Set([emptyLot.id]));
                  setEditModeId(emptyLot.id);
                }}
              />
            )}
          </div>
          <div className="border-subtle rounded-base">
            <table className="table-auto w-full border-collapse border-spacing-0 surface-elevated rounded-base">
              <thead>
                <tr>
                  <th className="typo-text-12 text-faded text-left py-2 px-4">{t('lots.name')}</th>
                  <th className="typo-text-12 text-faded text-left py-2 px-4">{t('lots.removal_date')}</th>
                  <th className="typo-text-12 text-faded text-left py-2 px-4">{t('lots.in_stock')}</th>
                  <th className="typo-text-12 text-faded text-left py-2 px-4">{t('lots.status')}</th>
                  <th className="typo-text-12 text-faded text-left py-2 px-4">{/* Actions */}</th>
                </tr>
              </thead>
              <tbody>
                {lots?.map(item => {
                  if (selectedIds.has(item.id)) {
                    return null;
                  }
                  return (
                    <LotTableRow
                      key={item.id}
                      lot={item}
                      disabled={editModeId !== null || item.isLocked}
                      tooltip={item.isLocked ? t('lots.locked_tooltip') : undefined}
                      onSelectClick={() => {
                        if (onDirectSelect) {
                          onDirectSelect(item.id);
                        } else {
                          setSelectedIds(new Set([item.id]));
                        }
                      }}
                    />
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
        {/* Hack to ensure there is enough vertical space to allow the datepicker to be shown without overflow */}
        <div className="min-h-[400px]" />
      </>
    );
  },
);
